import { createFileRoute } from '@tanstack/react-router'
import { Grid3X3, Search } from 'lucide-react'
import { useMemo, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import { MobileFilterDisclosure } from '~/components/data/MobileFilterDisclosure'
import {
  OrgsTable,
  organizationsKeys,
  type OrganizationListRow,
} from '~/features/organizations'
import { http } from '~/lib/http'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/organizations` — Organizations list surface (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `search`, `status`, `planTier`.
 *
 * Spec: 06-organizations.md §C.
 */
const orgsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
  search: z.string().optional(),
  status: z
    .enum(['all', 'active', 'trial', 'past_due', 'frozen'])
    .default('all'),
  planTier: z.string().optional(),
})
type OrgsSearch = z.infer<typeof orgsSearchSchema>

const URL_DEFAULTS: OrgsSearch = {
  page: 1,
  limit: 25,
  status: 'all',
}

const STATUS_CHIPS: ReadonlyArray<{
  readonly id: 'all' | 'active' | 'trial' | 'past_due' | 'frozen'
  readonly label: string
}> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'trial', label: 'Trial' },
  { id: 'past_due', label: 'Past Due' },
  { id: 'frozen', label: 'Frozen' },
]

const PLAN_CHIPS: readonly string[] = [
  'Free',
  'Pro',
  'Business',
  'Enterprise',
]

const ACTIVITY_CHIPS: readonly string[] = ['7d', '30d', 'Stale >90d']

export const Route = createFileRoute('/admin/organizations/')({
  validateSearch: orgsSearchSchema,
  component: OrganizationsListRoute,
})

interface OrgsListPayload {
  readonly organizations?: readonly OrganizationListRow[]
  readonly items?: readonly OrganizationListRow[]
  readonly total?: number
  readonly totals?: Record<string, number | undefined>
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

interface OrganizationsPage extends PaginatedResult<OrganizationListRow> {
  readonly totals?: Record<string, number | undefined>
}

async function fetchOrgsPage(
  params: PaginatedParams,
): Promise<OrganizationsPage> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
    offset: (params.page - 1) * params.limit,
  }
  if (params.search) query.search = params.search
  if (params.sort) query.sort = params.sort
  const status = params.filters?.status
  const planTier = params.filters?.planTier
  if (status && status !== 'all') query.status = status
  if (planTier) query.planTier = planTier

  const raw = await http.get<OrgsListPayload>(
    '/api/v1/admin/organizations',
    { query },
  )
  const rows = (raw.organizations ?? raw.items ?? []) as readonly OrganizationListRow[]
  const total = raw.pagination?.total ?? raw.total ?? rows.length
  const normalized = toPaginatedResult(rows, total, params, raw.pagination)
  return { ...normalized, ...(raw.totals ? { totals: raw.totals } : {}) }
}

function OrganizationsListRoute(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(orgsSearchSchema, URL_DEFAULTS)

  const filters = useMemo<Record<string, string | undefined>>(
    () => ({
      status: search.status,
      ...(search.planTier ? { planTier: search.planTier } : {}),
    }),
    [search.status, search.planTier],
  )
  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    search: search.search,
    filters,
  }

  const list = usePaginatedQuery<OrganizationListRow>({
    queryKey: [...organizationsKeys.all, 'list-paginated'],
    params,
    fetcher: fetchOrgsPage,
  })

  const organizations = list.data?.data ?? []
  const pagination = list.data?.pagination
  // `totals` field bleeds through via the OrganizationsPage extension —
  // pluck it safely.
  const totals = (list.data as OrganizationsPage | undefined)?.totals
  const totalLabel = list.data
    ? `${pagination?.total ?? organizations.length} total · ${
        totals?.past_due ?? 0
      } past due`
    : 'Loading…'
  const activeFilterCount =
    (search.status === 'all' ? 0 : 1) + (search.planTier ? 1 : 0)

  return (
    <div className="ka-page ka-orgs-page">
      <div className="ka-filter-strip ka-org-filter-strip" role="region" aria-label="Filters">
        <MobileFilterDisclosure
          id="organization-list-filters"
          activeCount={activeFilterCount}
        >
          <div className="ka-filter-group">
            <span className="ka-filter-group-label">Status</span>
            {STATUS_CHIPS.map((chip) => (
              <button
                type="button"
                key={chip.id}
                className={`ka-detail-chip ${
                  search.status === chip.id ? 'is-active' : ''
                }`}
                aria-pressed={search.status === chip.id}
                onClick={() => setUrl({ status: chip.id, page: 1 })}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <div className="ka-filter-group">
            <span className="ka-filter-group-label">Plan</span>
            {PLAN_CHIPS.map((plan) => (
              <button
                type="button"
                key={plan}
                className={`ka-detail-chip ${
                  search.planTier === plan ? 'is-active' : ''
                }`}
                aria-pressed={search.planTier === plan}
                onClick={() =>
                  setUrl({
                    planTier: search.planTier === plan ? undefined : plan,
                    page: 1,
                  })
                }
              >
                {plan}
              </button>
            ))}
          </div>
          <div className="ka-filter-group">
            <span className="ka-filter-group-label">Activity</span>
            {ACTIVITY_CHIPS.map((chip) => (
              <button
                type="button"
                key={chip}
                className="ka-detail-chip"
                aria-pressed="false"
              >
                {chip}
              </button>
            ))}
          </div>
        </MobileFilterDisclosure>
        <div className="ka-flex-spacer" />
        {search.search && (
          <button
            type="button"
            className="ka-detail-chip is-active"
            onClick={() => setUrl({ search: undefined, page: 1 })}
          >
            <Search size={12} aria-hidden="true" />
            {search.search}
          </button>
        )}
        <span className="ka-org-list-meta">{totalLabel}</span>
        <button type="button" className="ka-btn ka-btn--secondary ka-btn--sm">
          <Grid3X3 size={12} aria-hidden="true" /> Grid
        </button>
      </div>

      <OrgsTable
        organizations={organizations}
        isLoading={list.isPending}
        error={list.isError ? list.error : undefined}
      />
      <DataTablePagination
        page={pagination?.page ?? search.page}
        limit={pagination?.limit ?? search.limit}
        total={pagination?.total ?? organizations.length}
        isLoading={list.isPending}
        onChange={({ page, limit }) => setUrl({ page, limit })}
      />
    </div>
  )
}
